import { Button, Divider, Flex, Form, FormProps, Spin } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import { useCallback, useEffect } from 'react'
import { useSyncNumericInputs } from 'shared/hooks/useSyncNumericInputs'
import { FormValues } from '../../types'
import Amount from '../Amount'
import Name from '../Name'
import TotalAmount from '../TotalAmount'
import DonationContent from '../DonationContent'
import Terms from '../Terms'
import PaymentTypes from '../PaymentTypes'
import { ICryptoCurrency, INetwork } from '../../api/types'
import { useMinAmount } from '../Amount/hooks/useMinAmount'
import CountrySelect from '../CountrySelect'
import { convertAmountOnChange } from '../../utils'
import { useFiatProviders } from './hooks/useFiatProviders'
import { lang } from './lang'
import s from './s.module.css'
import { formKeys } from './constants'
import { useCryptoCurrencies } from './hooks/useCryptoCurrencies'
import { useFiatCurrencies } from './hooks/useFiatCurrencies'
import { useDefaultValues } from './hooks/useDefaultValues'
import { usePostDonation } from './hooks/usePostDonate'
import { useFinalCryptoAmount } from './hooks/useFinalCryptoAmount'
import { useDefaultCountry } from './hooks/useDefaultCountry'
import { useInitialConvertAmount } from './hooks/useInitialConvertAmount'
import { IFiatProvider, PaymentType } from '@/pages/Donation/api/streamer/types'
import { useStreamerConfigContext } from '@/contexts/StreamerConfigContext/StreamerConfigContext'
import Toggle from '@/shared/components/Toggle'
import CustomForm from '@/shared/components/CustomForm'
import { getCurrentBaseCurrencyOption } from '@/pages/Donation/components/Amount/utils.ts'
import Goal from '@/pages/Donation/components/Goal'

// eslint-disable-next-line max-lines-per-function, complexity
export default function DonationForm() {
  const intl = useIntl()

  const { baseCurrencies, cryptoCurrencies, isLoading: isCryptoCurrenciesLoading } = useCryptoCurrencies()
  const fiatCurrencies = useFiatCurrencies()
  const { username = '', config } = useStreamerConfigContext()
  const submitButtonText = config?.button_text ?? intl.formatMessage(lang.donateButton)

  const [form] = Form.useForm<FormValues>()
  const {
    baseCurrency,
    fiatAmount,
    isIncludeFee,
    paymentType,
    selectedCryptoAsset,
    selectedNetwork,
    customizeType = [],
    donationType,
    country,
    state,
  } = Form.useWatch<FormValues>([], form) ?? {}
  const isCryptoFlow = paymentType === PaymentType.Crypto

  useDefaultCountry(form)

  const initialValues = useDefaultValues({
    cryptoCurrencies,
    baseCurrencies,
  })

  useInitialConvertAmount({
    initialAmount: initialValues[formKeys.fiatAmount],
    baseCurrencyCode: initialValues[formKeys.baseCurrency]?.code,
    cryptoAssetCode: initialValues[formKeys.selectedCryptoAsset]?.code,
    onSetCryptoAmount: (amount: string) => form.setFieldValue(formKeys.cryptoAmount, amount),
  })

  const {
    handleFormValuesChange: handleFormAmountsChange,
    inputsState: amountInputsState,
    handleFirstInputChange: handleFiatAmountChange,
  } = useSyncNumericInputs({
    setFirstValue: (value: string) => form.setFieldValue(formKeys.fiatAmount, value),
    setSecondValue: (value: string) => form.setFieldValue(formKeys.cryptoAmount, value),
    calculateFirstValue: convertAmountOnChange,
    calculateSecondValue: async (...args) => {
      if (form.getFieldValue(formKeys.paymentType) === PaymentType.Crypto) {
        return convertAmountOnChange(...args)
      } else {
        return ''
      }
    },
  })

  const { minAmount: minAmountInBaseCurrency, isLoading: isMinAmountLoading } = useMinAmount({
    baseCurrencyCode: baseCurrency?.code,
    customizeType: customizeType || [],
    donationType,
  })

  const handleSetAndConvertFiatAmount = (...args: Parameters<typeof handleFiatAmountChange>) => {
    const [value, opts] = args
    form.setFieldValue(formKeys.fiatAmount, value)
    handleFiatAmountChange(value, opts)
  }

  const { fiatProviders, isProvidersLoading, isFiatProvidersError } = useFiatProviders({
    fiatAmount,
    baseCurrencyCode: baseCurrency?.code,
    setSelectedFiatProvider: (value: IFiatProvider | null) => form.setFieldValue(formKeys.selectedFiatProvider, value),
    country: country || '',
    state: state || undefined,
  })

  const {
    data: cryptoData,
    isLoading: isCryptoLoading,
    error: cryptoError,
  } = useFinalCryptoAmount({
    cryptoAssetCode: selectedCryptoAsset?.code,
    baseCurrencyCode: baseCurrency?.code,
    networkCode: selectedNetwork?.code,
    fiatAmount,
    isIncludeFee,
    paymentType,
    minAmountInBaseCurrency,
  }) ?? {}

  const handlePaymentTypeChange = useCallback(
    (type: PaymentType) => {
      const currenciesList = type === PaymentType.Fiat ? fiatCurrencies : baseCurrencies
      const currentOption = getCurrentBaseCurrencyOption(currenciesList, baseCurrency?.code)
      form.setFieldValue(formKeys.baseCurrency, currentOption)

      if (type === PaymentType.Fiat) {
        form.resetFields([formKeys.cryptoAmount])
      } else {
        handleFiatAmountChange(fiatAmount, {
          force: true,
          args: { username, currencyFrom: baseCurrency?.code, currencyTo: selectedCryptoAsset?.code },
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseCurrencies, baseCurrency?.code, fiatAmount, fiatCurrencies, form, selectedCryptoAsset?.code, username]
  )

  const handleSetCryptoAsset = useCallback(
    (asset: ICryptoCurrency | null, networkCode?: string) => {
      if (selectedCryptoAsset?.code === asset?.code) {
        return
      }

      let network: INetwork | undefined
      if (asset?.networks?.length) {
        network = networkCode ? asset.networks.find(n => n.code === networkCode) : asset.networks[0]
      }

      form.setFieldsValue({ [formKeys.selectedCryptoAsset]: asset, [formKeys.selectedNetwork]: network ?? null })
      handleFiatAmountChange(fiatAmount, {
        force: true,
        args: {
          username,
          currencyFrom: baseCurrency?.code,
          currencyTo: asset?.code,
        },
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseCurrency?.code, fiatAmount, form, selectedCryptoAsset?.code, username]
  )

  const handleFormValuesChange = useCallback(
    (...args: Parameters<Required<FormProps>['onValuesChange']>) => {
      const [changes] = args
      const isFiatChanged = formKeys.fiatAmount in changes
      const baseCurrencyCode = form.getFieldValue(formKeys.baseCurrency)?.code
      const cryptoAssetCode = form.getFieldValue(formKeys.selectedCryptoAsset)?.code
      const currencyFrom = isFiatChanged ? baseCurrencyCode : cryptoAssetCode
      const currencyTo = isFiatChanged ? cryptoAssetCode : baseCurrencyCode

      handleFormAmountsChange({
        fields: [formKeys.fiatAmount, formKeys.cryptoAmount],
        callbackArgs: { username: username ?? '', currencyFrom, currencyTo },
        formArgs: args,
      })
    },
    [form, handleFormAmountsChange, username]
  )

  const currentFiatCurrenciesList = paymentType === PaymentType.Fiat ? fiatCurrencies : baseCurrencies
  const finalAmount = isCryptoFlow ? cryptoData?.final_amount : fiatAmount
  const fee = isCryptoFlow ? cryptoData?.fee : undefined
  const isFinalAmountLoading = isCryptoFlow ? isCryptoLoading : isProvidersLoading
  const isFinalAmountError = isCryptoFlow ? !!cryptoError : isFiatProvidersError
  const isRequestsLoading =
    isFinalAmountLoading ||
    isMinAmountLoading ||
    isProvidersLoading ||
    amountInputsState.first.isLoading ||
    amountInputsState.second.isLoading
  const isDonateDisabled = !!(
    isRequestsLoading ||
    isFinalAmountError ||
    !fiatAmount ||
    amountInputsState.first.errorCode ||
    (isCryptoFlow && amountInputsState.second.errorCode)
  )

  const {
    postDonate,
    isLoading: isPostDonateLoading,
    isRequestError: isPostDonateRequestError,
    reset: resetPostDonate,
  } = usePostDonation({ isDisabled: isDonateDisabled })

  useEffect(() => {
    if (isPostDonateRequestError) {
      resetPostDonate()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseCurrency, selectedCryptoAsset, selectedNetwork, fiatAmount, paymentType, country, resetPostDonate])

  return (
    <>
      {isPostDonateLoading && (
        <Spin indicator={<LoadingOutlined style={{ color: 'var(--accent-light)' }} />} fullscreen />
      )}
      <CustomForm
        layout="vertical"
        form={form}
        initialValues={initialValues}
        onFinish={postDonate}
        scrollToFirstError={{ behavior: 'smooth' }}
        onValuesChange={handleFormValuesChange}
      >
        <Flex vertical gap={16}>
          <PaymentTypes
            fiatProviders={fiatProviders}
            isProvidersLoading={isProvidersLoading}
            cryptoCurrencies={cryptoCurrencies}
            onSetCryptoAsset={handleSetCryptoAsset}
            onPaymentTypeChange={handlePaymentTypeChange}
            isCryptoOptionsLoading={isCryptoCurrenciesLoading}
          />
          {paymentType === PaymentType.Fiat && <CountrySelect form={form} />}
          <Amount
            baseCurrencies={currentFiatCurrenciesList}
            paymentType={paymentType}
            selectedCryptoAssetCode={selectedCryptoAsset?.code}
            isFinalAmountError={isFinalAmountError}
            minAmountInBaseCurrency={minAmountInBaseCurrency}
            isMinAmountLoading={isMinAmountLoading}
            isFinalAmountLoading={isFinalAmountLoading}
            isPostDonateRequestError={isPostDonateRequestError}
            inputsState={amountInputsState}
            onSetFiatValue={handleSetAndConvertFiatAmount}
          />
          <Name form={form} />
          <DonationContent onSetFiatAmount={handleSetAndConvertFiatAmount} />
          <Goal form={form} />
          {paymentType === PaymentType.Crypto && (
            <Form.Item name={formKeys.isIncludeFee} noStyle>
              <Toggle
                label={intl.formatMessage(lang.supportAgree, { name: username ?? '' })}
                size="small"
                onChange={resetPostDonate}
              />
            </Form.Item>
          )}
        </Flex>
        <Divider />
        <TotalAmount
          paymentType={paymentType}
          cryptoAssetCode={selectedCryptoAsset?.code}
          baseCurrencyCode={baseCurrency?.code}
          finalAmount={finalAmount}
          fee={fee}
          isLoading={isFinalAmountLoading}
        />
        <Form.Item noStyle>
          <Button type="primary" size="large" block htmlType="submit" className={s.button}>
            {submitButtonText}
          </Button>
        </Form.Item>
        <Terms />
      </CustomForm>
    </>
  )
}
